import { useEffect, useMemo, useState } from "react";
import { api, eur, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Minus, Trash2, Search } from "lucide-react";

// Éditeur de commande partagé POS / Client : quantités, retrait, ajout d'articles. Prix recalculés côté serveur.
export default function OrderEditDialog({ open, order, slug, onClose, onSaved, save, light = false, notice = null, reduceOnly = false }) {
  const [lines, setLines] = useState([]);
  const [menu, setMenu] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open || !order) return;
    setLines((order.items || []).map((it, i) => ({ key: i, ...it, quantity: it.quantity || 1 })));
    setQ("");
    api.getMenu(slug).then((m) => setMenu(m.products || [])).catch(() => {});
  }, [open, order, slug]);
  const filtered = useMemo(() => (q ? menu.filter((p) => p.available !== false && p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 8) : []), [menu, q]);
  const setQty = (key, d) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, quantity: Math.max(1, l.quantity + d) } : l)));
  const remove = (key) => setLines((ls) => ls.filter((l) => l.key !== key));
  const add = (p) => {
    setLines((ls) => {
      const ex = ls.find((l) => l.product_id === p.id && !(l.options || []).length && !(l.supplements || []).length);
      if (ex) return ls.map((l) => (l === ex ? { ...l, quantity: l.quantity + 1 } : l));
      return [...ls, { key: Date.now(), product_id: p.id, name: p.name, base_price: p.price, quantity: 1, line_total: p.price, options: [], supplements: [] }];
    });
    setQ("");
  };
  const total = lines.reduce((s, l) => s + (l.unit_price || l.base_price || 0) * l.quantity, 0);
  const submit = async () => {
    if (!lines.length) return toast.error("Une commande doit contenir au moins un article");
    setBusy(true);
    try {
      const items = lines.map(({ product_id, name, base_price, quantity, options, supplements, formula }) => ({ product_id, name, base_price, quantity, line_total: (base_price || 0) * quantity, options: options || [], supplements: supplements || [], formula: formula || [] }));
      const o = await save(items);
      if (!notice) toast.success("Commande modifiée");
      onSaved?.(o); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Modification impossible"); }
    finally { setBusy(false); }
  };
  const bg = light ? "bg-[#FBF9F5] text-[#123026] border-none" : "bg-[#0C1713] border-[#1B3028] text-stone-100";
  const row = light ? "bg-white border-[#EAE5DC]" : "bg-[#111F1A] border-[#1B3028]";
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="order-edit-dialog" className={`rounded-3xl max-w-md w-[calc(100%-1.5rem)] ${bg}`}>
        <DialogHeader><DialogTitle className="font-serif-display text-2xl">Modifier la commande</DialogTitle>
          <DialogDescription className={light ? "text-[#8A918D]" : "text-stone-400"}>{order?.order_code || order?.order_number} · Table {order?.table_number}</DialogDescription></DialogHeader>
        {notice && <p data-testid="order-edit-notice" className="rounded-xl bg-[#FFF6E5] border border-[#F3DDB0] px-3 py-2 text-xs text-[#8A5A00]">{notice}</p>}
        <div className="space-y-2 max-h-[40vh] overflow-y-auto no-scrollbar">
          {lines.map((l) => (
            <div key={l.key} data-testid={`edit-line-${l.product_id}`} className={`flex items-center gap-2 rounded-2xl border p-2.5 ${row}`}>
              <div className="flex-1 min-w-0"><p className="text-sm font-semibold truncate">{l.name}</p><p className={`text-xs ${light ? "text-[#8A918D]" : "text-stone-500"}`}>{eur(l.unit_price || l.base_price || 0)} × {l.quantity}</p></div>
              <button data-testid={`edit-dec-${l.product_id}`} onClick={() => setQty(l.key, -1)} className={`w-8 h-8 rounded-full grid place-items-center ${light ? "bg-[#EBF2EE]" : "bg-[#1B3028]"}`}><Minus className="w-3.5 h-3.5" /></button>
              <span data-testid={`edit-qty-${l.product_id}`} className="w-5 text-center text-sm font-bold">{l.quantity}</span>
              {!reduceOnly && <button data-testid={`edit-inc-${l.product_id}`} onClick={() => setQty(l.key, 1)} className="w-8 h-8 rounded-full grid place-items-center bg-[#C69C4A] text-[#0C1713]"><Plus className="w-3.5 h-3.5" /></button>}
              <button data-testid={`edit-remove-${l.product_id}`} onClick={() => remove(l.key)} className="w-8 h-8 rounded-full grid place-items-center text-red-400 hover:bg-red-500/10 ml-1"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
        {!reduceOnly && <div className="relative">
          <Search className={`w-4 h-4 absolute left-3 top-3 ${light ? "text-[#8A918D]" : "text-stone-500"}`} />
          <Input data-testid="edit-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ajouter un article…" className={`pl-9 rounded-xl h-10 ${light ? "bg-white border-[#EAE5DC]" : "bg-[#111F1A] border-[#1B3028] text-stone-100"}`} />
          {filtered.length > 0 && (
            <div className={`absolute z-10 left-0 right-0 mt-1 rounded-2xl border overflow-hidden shadow-xl ${row}`}>
              {filtered.map((p) => (
                <button key={p.id} data-testid={`edit-add-${p.id}`} onClick={() => add(p)} className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${light ? "hover:bg-[#EBF2EE]" : "hover:bg-[#1B3028]"}`}>
                  {p.image && <img src={p.image} alt="" className="w-8 h-8 rounded-lg object-cover" />}<span className="flex-1 truncate">{p.name}</span><span className="text-[#C69C4A] font-semibold">{eur(p.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>}
        <DialogFooter className="flex-row items-center justify-between gap-2">
          <span data-testid="edit-total" className="text-lg font-bold text-[#C69C4A]">{eur(total)}</span>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} className={`rounded-full ${light ? "border-[#DDE5E0] bg-white" : "bg-transparent border-[#1B3028] text-stone-300"}`}>Annuler</Button>
            <Button data-testid="edit-save" onClick={submit} disabled={busy} className="rounded-full bg-[#C69C4A] text-[#0C1713] hover:bg-[#B38B3A] font-bold">{busy ? "…" : "Enregistrer"}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
